/*
 * A work queue (task 38.04): records worked through one after another — the
 * Purchases › Inbox To do list, each document made into an expense in turn.
 * Configured as `queue` on a page in pageViewData:
 *
 *   entity     the records of the queue ('inboxDocument')
 *   filter     which of them are still to do, in the list filter dialect
 *              ('status:new;archived:false')
 *   orderBy, direction   the order they are worked in — the list's own
 *   path       where one opens, with :id ('/purchases/expense/newFromInbox/:id')
 *   donePath   where to go when none is left ('/purchases/inbox')
 *   label      the progress text, "${1} of ${2}"
 *
 * The pure part, here: where the current record stands and which comes next.
 * `done` is what this run has already finished — a saved record can still be
 * listed as to do for a moment, until the server's event has moved it on.
 */

export const queueStep = ({ ids = [], done = [], current }) => {
    const finished = new Set(done)
    const remaining = ids.filter(id => !finished.has(id))
    if (current && !remaining.includes(current) && !finished.has(current)) remaining.unshift(current)

    // Next: the first one after the current in the list's order, else the
    // first before it — never the current or anything already done.
    const at = ids.indexOf(current)
    const after = ids.slice(at + 1).find(id => !finished.has(id) && id !== current)
    const before = ids.slice(0, Math.max(at, 0)).find(id => !finished.has(id) && id !== current)

    return {
        position: finished.size + 1,
        total: finished.size + remaining.length,
        next: after ?? before ?? null
    }
}

// This run's finished records, per queue, for the browser tab's lifetime.
const storageKey = (queue) => `workQueue:${queue.entity}`

export const readDone = (queue) => {
    try { return JSON.parse(sessionStorage.getItem(storageKey(queue)) || '[]') } catch { return [] }
}

export const writeDone = (queue, done) => {
    try { sessionStorage.setItem(storageKey(queue), JSON.stringify(done)) } catch { /* private mode: progress restarts, nothing breaks */ }
}

export const queueParams = (queue) => new URLSearchParams({
    filter: queue.filter,
    orderBy: queue.orderBy,
    direction: queue.direction || 'asc',
    noPagination: 'true'
})
